import { mountInputFile, runFFmpeg } from './ffmpeg';

export interface StreamInfo {
  codec: string;
  raw: string;
}

export interface VideoStream extends StreamInfo {
  width: number | null;
  height: number | null;
  fps: number | null;
  bitrate: number | null;
  /** Pixel format string as ffmpeg reports it — e.g. yuv420p, yuv420p10le. */
  pixFmt: string | null;
  /** True when the source is 10-bit or higher (usually HDR or high-quality prosumer). */
  highBitDepth: boolean;
  /** True when ffmpeg tags the stream with an HDR-capable colour space. */
  hdr: boolean;
  /** Transfer function of a real HDR source: PQ (HDR10, Dolby Vision 8.1)
   *  or HLG (iPhone Dolby Vision 8.4, broadcast). Null for SDR, including
   *  SDR BT.2020. Tone mapping to SDR keys off this rather than `hdr`. */
  hdrTransfer: 'pq' | 'hlg' | null;
  /** Clockwise rotation the container asks players to apply (0/90/180/270).
   *  A portrait iPhone clip stores landscape pixels and carries 90 here. */
  rotationDegrees: number;
  /** True when the source's frame timing looks variable (screen-recorders, some phone captures). */
  variableFramerate: boolean;
  /** Sample aspect ratio (pixel shape). 1 = square pixels (the default);
   *  DVDs, HDV cameras, and old TV captures use non-square pixels — a
   *  720×480 NTSC-DVD frame is stored non-square but displayed 16:9. If we
   *  transcode without correcting this, playback comes out squeezed. `null`
   *  when ffmpeg didn't report it (treat as 1). */
  sampleAspectRatio: number | null;
  /** `width × height × SAR`, the intended display width in square pixels.
   *  Height unchanged. Null when width or SAR is null. Consumers should use
   *  this when picking encode dimensions to fix anamorphic sources. */
  displayWidth: number | null;
  /** True when ffmpeg tags the stream as interlaced. Interlaced sources
   *  played on progressive displays without deinterlacing show combing;
   *  transcode paths apply `yadif=1` when this is set. */
  interlaced: boolean;
}

export interface SubtitleStream extends StreamInfo {
  /** True when the codec is text-based (SubRip / ASS / SSA / WebVTT /
   *  mov_text / MicroDVD). Text subs can be converted to `mov_text` and
   *  carried inside MP4. Bitmap subs (PGS from Blu-ray, VobSub from DVD)
   *  cannot go into MP4 without a separate demuxable file, so Wave drops
   *  them and warns rather than failing the whole convert. */
  text: boolean;
}

export interface AudioStream extends StreamInfo {
  sampleRate: number | null;
  /** Raw ffmpeg description, e.g. "stereo", "5.1(side)", "7.1". Kept for
   *  display; consumers wanting an integer should use `numChannels`. */
  channels: string | null;
  /** Channel count normalised from the raw string. `null` if unrecognised;
   *  callers should default to 2 (stereo) in that case. AAC-LC supports up
   *  to 6 channels (5.1), so anything larger is clamped in transcode paths. */
  numChannels: number | null;
  bitrate: number | null;
}

export interface MediaInfo {
  filename: string;
  sizeBytes: number;
  container: string | null;
  durationSeconds: number | null;
  overallBitrate: number | null;
  video: VideoStream | null;
  audio: AudioStream | null;
  /** Every subtitle stream in the source. Empty when there are none.
   *  Consumers use `text` to decide whether to preserve into MP4 as
   *  `mov_text` or silently drop (bitmap subs). */
  subtitles: SubtitleStream[];
  rawLog: string;
  /** Non-null when this source is a raw video elementary stream ffmpeg
   *  can't sniff on its own (H.264 / HEVC / VC-1 Annex B, some Dahua DAV
   *  captures). The value is the ffmpeg `-f` name to inject before `-i`
   *  on every subsequent ffmpeg invocation. Set by inspectFile after a
   *  byte-level check confirms the file's leading bytes match the
   *  extension's claim — never trust the extension alone. */
  rawStreamFormat: string | null;
  /** True when the browser's own VideoDecoder accepts this file's video
   *  track, so a re-encode can decode on the graphics chip (Path C) instead
   *  of in ffmpeg-wasm. Checked against the real track: Chrome only reports
   *  HEVC support when it sees the stream's own parameter sets. */
  hardwareDecodable: boolean;
}

const extensionOf = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : 'bin';
};

/** Returns the ffmpeg `-f` format name suggested by a file extension for raw
 *  elementary streams that carry no enclosing container. Null for any ext
 *  that names a real container (mp4, mkv, avi, etc.) — ffmpeg auto-detects
 *  those from the byte stream. `dav` is Dahua's CCTV container; well-formed
 *  Dahua footage is a plain H.264 Annex B stream with a proprietary header
 *  ffmpeg sometimes skips, so hint h264 here to keep the demuxer honest. */
export function rawStreamFormat(ext: string): string | null {
  switch (ext.toLowerCase()) {
    case '264': case 'h264': case 'avc': return 'h264';
    case '265': case 'h265': case 'hevc': return 'hevc';
    case 'vc1': return 'vc1';
    case 'dav': return 'h264';
    default: return null;
  }
}

/** Content-sniff for a raw video elementary stream. H.264, HEVC and VC-1
 *  Annex B / EP-frame layouts all begin with a NAL start code — either the
 *  3-byte `00 00 01` or the 4-byte `00 00 00 01`. When a user renames a
 *  proper container to `.h264` / `.dav` / etc., the bytes will NOT match
 *  and we must skip the `-f` hint so ffmpeg can auto-detect the real
 *  container instead of crashing while trying to parse an MP4 as a raw
 *  H.264 stream. */
function looksLikeRawAnnexB(head: Uint8Array): boolean {
  if (head.length < 3) return false;
  const b0 = head[0], b1 = head[1], b2 = head[2], b3 = head.length >= 4 ? head[3] : -1;
  if (b0 === 0 && b1 === 0 && b2 === 1) return true;
  if (b0 === 0 && b1 === 0 && b2 === 0 && b3 === 1) return true;
  return false;
}

/** Resolves the raw-stream format ffmpeg should be forced into for this
 *  source, or `null` if the file should use ffmpeg's normal auto-detection.
 *  Combines an extension hint with a byte-level sniff so a mislabeled file
 *  (e.g. a `.mkv` renamed to `.h264`) doesn't get force-parsed as raw. */
export async function resolveRawStreamFormat(file: File): Promise<string | null> {
  const ext = extensionOf(file.name);
  const hint = rawStreamFormat(ext);
  if (!hint) return null;
  try {
    const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
    return looksLikeRawAnnexB(head) ? hint : null;
  } catch {
    // If we can't read the first few bytes, err on the safe side and let
    // ffmpeg auto-detect rather than force a hint that could crash it.
    return null;
  }
}

/**
 * Runs `ffmpeg -i <input>` and parses its stderr to a structured MediaInfo.
 * Deliberately does not care about the exit code — with no output specified,
 * ffmpeg exits non-zero but still prints full metadata to stderr.
 *
 * Uses WORKERFS mount to avoid copying the file into the wasm heap for the
 * inspect call — a 1 GB file with a MEMFS write would spend 1 GB of RAM just
 * to read a few metadata lines.
 */
export async function inspectFile(file: File): Promise<MediaInfo> {
  const ext = extensionOf(file.name);
  const rawFmt = await resolveRawStreamFormat(file);
  const mount = await mountInputFile(file, `input.${ext}`);
  try {
    const fmtArgs = rawFmt ? ['-f', rawFmt] : [];
    const { logs } = await runFFmpeg(['-hide_banner', ...fmtArgs, '-i', mount.path]);
    const info = parseInspectLog(logs.join('\n'), file);
    info.rawStreamFormat = rawFmt;
    info.hardwareDecodable = await checkHardwareDecode(file, info);
    return info;
  } finally {
    await mount.unmount();
  }
}

// Codecs WebCodecs' VideoDecoder can take, by ffmpeg's name for them.
const HW_DECODE_CODECS = new Set(['h264', 'hevc', 'vp8', 'vp9', 'av1']);
// Containers the Path C demuxer reads; keep in step with FORMATS in hwdecode.ts.
const HW_DECODE_CONTAINERS = /\.(mp4|m4v|mov|qt|mkv|webm)$/i;

/** Whether Path C can decode this file: 8-bit SDR, progressive video in a
 *  container the demuxer reads, and a track the browser's decoder accepts.
 *  10-bit and HDR frames come out of hardware decoders in formats that vary
 *  by GPU and couldn't be verified, so they stay on Path B and its tested
 *  tone mapping. Opening the track reads only its headers. */
async function checkHardwareDecode(file: File, info: MediaInfo): Promise<boolean> {
  const v = info.video;
  if (!v || v.highBitDepth || v.hdr || v.interlaced || info.rawStreamFormat) return false;
  if (!HW_DECODE_CODECS.has(v.codec.toLowerCase()) || !HW_DECODE_CONTAINERS.test(file.name)) return false;
  if (typeof VideoDecoder === 'undefined') return false;
  try {
    const { openHwDecode } = await import('./hwdecode');
    const source = await openHwDecode(file);
    source?.input.dispose();
    return !!source;
  } catch {
    return false;
  }
}

export function parseInspectLog(rawLog: string, file: File): MediaInfo {
  return {
    filename: file.name,
    sizeBytes: file.size,
    container: parseContainer(rawLog),
    durationSeconds: parseDuration(rawLog),
    overallBitrate: parseOverallBitrate(rawLog),
    video: parseVideoStream(rawLog),
    audio: parseAudioStream(rawLog),
    subtitles: parseSubtitleStreams(rawLog),
    rawLog,
    // inspectFile overwrites this after byte-sniffing; parseInspectLog
    // callers that skip the sniff get a safe default of null (auto-detect).
    rawStreamFormat: null,
    // Set by inspectFile after opening the track; false keeps Path B.
    hardwareDecodable: false,
  };
}

function parseContainer(log: string): string | null {
  const m = log.match(/Input #0,\s*([^,]+),/);
  if (!m) return null;
  const first = m[1].split(',')[0].trim();
  return first || null;
}

function parseDuration(log: string): number | null {
  const m = log.match(/Duration:\s*(\d+):(\d{2}):(\d{2}(?:\.\d+)?)/);
  if (!m) return null;
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]);
}

function parseOverallBitrate(log: string): number | null {
  const m = log.match(/bitrate:\s*(\d+)\s*kb\/s/);
  return m ? Number(m[1]) * 1000 : null;
}

function parseVideoStream(log: string): VideoStream | null {
  const m = log.match(/Stream #\d+:\d+[^\n]*: Video:\s*([^\n]+)/);
  if (!m) return null;
  const raw = m[1].trim();
  const codec = raw.split(/[\s(,]/)[0];

  const size = raw.match(/(\d{2,5})x(\d{2,5})/);
  const fps = raw.match(/([\d.]+)\s*fps/);
  // "23.98 fps, 24 tbr" — when fps and tbr disagree significantly, the source
  // is variable-framerate. Screen recordings, some phone captures do this.
  const tbr = raw.match(/([\d.]+)\s*tbr/);
  const bitrate = raw.match(/(\d+)\s*kb\/s/);
  // Pixel format shows up near the codec — e.g. "h264 (High), yuv420p(tv, bt709)".
  const pixMatch = raw.match(/\b(yuv[0-9a-z]+|nv1[26]|rgb[0-9a-z]*|gbrp[0-9a-z]*|gray[0-9a-z]*)\b/);
  // Rotation shows up on a nearby "displaymatrix" or "rotate" line in the log.
  // The old "rotate" tag is clockwise; ffmpeg prints the display matrix angle
  // counter-clockwise, so a portrait iPhone clip reads "rotation of -90.00
  // degrees" and means 90 clockwise.
  const rotTag = log.match(/rotate\s*[:=]\s*(-?\d+)/i);
  const rotMatrix = rotTag ? null : log.match(/displaymatrix.*rotation of\s*(-?\d+(?:\.\d+)?)/i);

  const pixFmt = pixMatch ? pixMatch[1] : null;
  const highBitDepth = pixFmt ? /10le|10be|12le|12be|16le|16be|p010|p016/.test(pixFmt) : false;
  const hdr = /\bbt2020\b|\bsmpte2084\b|\barib-std-b67\b/i.test(raw);
  const hdrTransfer: 'pq' | 'hlg' | null = /\bsmpte2084\b/i.test(raw)
    ? 'pq'
    : /\barib-std-b67\b/i.test(raw) ? 'hlg' : null;

  let rotationDegrees = 0;
  if (rotTag || rotMatrix) {
    const n = rotTag ? Math.round(Number(rotTag[1])) : -Math.round(Number(rotMatrix![1]));
    // Normalise to 0/90/180/270.
    rotationDegrees = ((n % 360) + 360) % 360;
    if (rotationDegrees === 90 || rotationDegrees === 180 || rotationDegrees === 270) {
      // keep
    } else if (rotationDegrees > 45 && rotationDegrees < 135) rotationDegrees = 90;
    else if (rotationDegrees > 135 && rotationDegrees < 225) rotationDegrees = 180;
    else if (rotationDegrees > 225 && rotationDegrees < 315) rotationDegrees = 270;
    else rotationDegrees = 0;
  }

  const fpsVal = fps ? Number(fps[1]) : null;
  const tbrVal = tbr ? Number(tbr[1]) : null;
  const variableFramerate =
    fpsVal != null && tbrVal != null && Math.abs(fpsVal - tbrVal) > 0.5;

  // SAR shows up in the stream line as e.g. "[SAR 32:27 DAR 16:9]". A value
  // ≠ 1 means non-square pixels — usually an anamorphic DVD / HDV source.
  // If SAR is missing we treat as 1 (square), which is the norm for
  // internet video.
  const sarMatch = raw.match(/\bSAR\s+(\d+):(\d+)\b/i);
  let sampleAspectRatio: number | null = null;
  if (sarMatch) {
    const n = Number(sarMatch[1]);
    const d = Number(sarMatch[2]);
    if (n > 0 && d > 0) sampleAspectRatio = n / d;
  }
  const widthVal = size ? Number(size[1]) : null;
  const heightVal = size ? Number(size[2]) : null;
  const displayWidth =
    widthVal != null && sampleAspectRatio != null
      ? Math.max(2, Math.round(widthVal * sampleAspectRatio / 2) * 2)
      : widthVal;

  // Interlaced sources: ffmpeg prints "tt" / "bb" / "tb" / "bt" as the field
  // order (top-first, bottom-first, etc.), or the "interlaced" keyword on
  // some builds. `progressive` explicitly means non-interlaced — check both.
  const interlaced = /\btop first\b|\bbottom first\b|\binterlaced\b|\btff\b|\bbff\b/i.test(raw)
    && !/\bprogressive\b/i.test(raw);

  return {
    codec,
    raw,
    width: widthVal,
    height: heightVal,
    fps: fpsVal,
    bitrate: bitrate ? Number(bitrate[1]) * 1000 : null,
    pixFmt,
    highBitDepth,
    hdr,
    hdrTransfer,
    rotationDegrees,
    variableFramerate,
    sampleAspectRatio,
    displayWidth,
    interlaced,
  };
}

function parseAudioStream(log: string): AudioStream | null {
  const m = log.match(/Stream #\d+:\d+[^\n]*: Audio:\s*([^\n]+)/);
  if (!m) return null;
  const raw = m[1].trim();
  const codec = raw.split(/[\s(,]/)[0];

  const sample = raw.match(/(\d{4,6})\s*Hz/);
  const channelsMatch = raw.match(/(?:Hz,\s*)([^,]+)/);
  const bitrate = raw.match(/(\d+)\s*kb\/s/);
  const channelsStr = channelsMatch ? channelsMatch[1].trim() : null;

  return {
    codec,
    raw,
    sampleRate: sample ? Number(sample[1]) : null,
    channels: channelsStr,
    numChannels: mapChannelString(channelsStr),
    bitrate: bitrate ? Number(bitrate[1]) * 1000 : null,
  };
}

/** ffmpeg's channel-layout strings map to fixed counts. Covers the layouts
 *  Wave actually sees in the wild; anything else returns null so the caller
 *  can default. */
function mapChannelString(s: string | null): number | null {
  if (!s) return null;
  const clean = s.trim().toLowerCase();
  if (clean.startsWith('mono')) return 1;
  if (clean.startsWith('stereo')) return 2;
  if (clean.startsWith('2.1')) return 3;
  if (clean.startsWith('3.0') || clean.startsWith('surround')) return 3;
  if (clean.startsWith('quad') || clean.startsWith('4.0')) return 4;
  if (clean.startsWith('4.1')) return 5;
  if (clean.startsWith('5.0')) return 5;
  if (clean.startsWith('5.1')) return 6;
  if (clean.startsWith('6.0') || clean.startsWith('hexagonal')) return 6;
  if (clean.startsWith('6.1')) return 7;
  if (clean.startsWith('7.0')) return 7;
  if (clean.startsWith('7.1')) return 8;
  // Numeric fallback: "n channels" or bare "n.n" — parse first int found.
  const num = clean.match(/^(\d+)\s*(?:channels?)?/);
  if (num) {
    const n = Number(num[1]);
    if (Number.isFinite(n) && n >= 1 && n <= 24) return n;
  }
  return null;
}

export function friendlyResolution(w: number | null, h: number | null): string | null {
  if (!w || !h) return null;
  const shortEdge = Math.min(w, h);
  const longEdge = Math.max(w, h);
  if (shortEdge >= 2160 || longEdge >= 3840) return '4K';
  if (shortEdge >= 1440) return '1440p';
  if (shortEdge >= 1080) return '1080p';
  if (shortEdge >= 720) return '720p';
  if (shortEdge >= 480) return '480p';
  if (shortEdge >= 360) return '360p';
  return `${w}x${h}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

/** Text subtitle codecs Wave can convert to `mov_text` for MP4 output.
 *  Everything else (PGS, VobSub, DVB) is bitmap and can't live in MP4. */
const TEXT_SUBTITLE_CODECS = new Set([
  'subrip', 'srt', 'ass', 'ssa', 'webvtt', 'mov_text', 'text', 'microdvd', 'jacosub',
]);

function parseSubtitleStreams(log: string): SubtitleStream[] {
  const out: SubtitleStream[] = [];
  // Each subtitle stream appears on its own line: "Stream #0:2(eng): Subtitle: subrip"
  const re = /Stream #\d+:\d+[^\n]*: Subtitle:\s*([^\n]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(log)) !== null) {
    const raw = m[1].trim();
    const codec = raw.split(/[\s(,]/)[0].toLowerCase();
    out.push({ codec, raw, text: TEXT_SUBTITLE_CODECS.has(codec) });
  }
  return out;
}

export function formatDuration(seconds: number | null): string {
  if (seconds == null || !isFinite(seconds)) return '—';
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}h ${m}m ${sec}s`
    : m > 0
      ? `${m}m ${sec}s`
      : `${sec}s`;
}
